import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, MoreHorizontal, Copy } from "lucide-react";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

interface Page {
  _id: string;
  title: string;
  route: string;
  createdAt: string;
  updatedAt: string;
}

export function Pages() {
  const navigate = useNavigate();
  const [pages, setPages] = useState<Page[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [route, setRoute] = useState("");
  const [pageType, setPageType] = useState<"html" | "markdown" | "redirect">("html");
  const [isCreating, setIsCreating] = useState(false);
  const [deletingPageId, setDeletingPageId] = useState<string | null>(null);
  const [cloningPageId, setCloningPageId] = useState<string | null>(null);
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const response = await fetch("/api/pages");

        if (response.ok) {
          const data = await response.json();
          setPages(data.pages || []);
        } else {
          console.error("Failed to fetch pages");
        }

        setBreadcrumbs([
          { label: "Pages", path: "/pages" },
        ]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();

    return () => {
      clearBreadcrumbs();
    };
  }, []);

  const handleCreatePage = async () => {
    if (!route.trim()) {
      return;
    }

    setIsCreating(true);
    try {
      const response = await fetch("/api/pages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ route: route.trim(), type: pageType }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsDialogOpen(false);
        setRoute("");
        setPageType("html");
        // Refresh the pages list
        const refreshResponse = await fetch("/api/pages");
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setPages(refreshData.pages || []);
        }
        navigate(`/pages/${data.page._id}/edit`);
      } else {
        const errorData = await response.json();
        console.error("Failed to create page:", errorData.error);
        alert(errorData.error || "Failed to create page");
      }
    } catch (error) {
      console.error("Failed to create page:", error);
      alert("An error occurred while creating the page");
    } finally {
      setIsCreating(false);
    }
  };

  const handleClonePage = async ({ pageId }: { pageId: string }) => {
    setCloningPageId(pageId);
    try {
      const response = await fetch(`/api/pages/${pageId}/clone`, {
        method: "POST",
      });

      if (response.ok) {
        const data = await response.json();
        const refreshResponse = await fetch("/api/pages");
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setPages(refreshData.pages || []);
        }
        navigate(`/pages/${data.page._id}/edit`);
      } else {
        const errorData = await response.json();
        console.error("Failed to clone page:", errorData.error);
        alert(errorData.error || "Failed to clone page");
      }
    } catch (error) {
      console.error("Failed to clone page:", error);
      alert("An error occurred while cloning the page");
    } finally {
      setCloningPageId(null);
    }
  };

  const handleDeletePage = async ({ pageId }: { pageId: string }) => {
    const page = pages.find((p) => p._id === pageId);
    const confirmed = window.confirm(
      `Are you sure you want to delete "${page?.title || page?.route}"? This action cannot be undone.`
    );

    if (!confirmed) return;

    setDeletingPageId(pageId);
    try {
      const response = await fetch(`/api/pages/${pageId}`, {
        method: "DELETE",
      });

      if (response.ok) {
        // Refresh the pages list
        const refreshResponse = await fetch("/api/pages");
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setPages(refreshData.pages || []);
        }
      } else {
        const errorData = await response.json();
        console.error("Failed to delete page:", errorData.error);
        alert(errorData.error || "Failed to delete page");
      }
    } catch (error) {
      console.error("Failed to delete page:", error);
      alert("An error occurred while deleting the page");
    } finally {
      setDeletingPageId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Pages</h1>
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Page</DialogTitle>
            <DialogDescription>
              Enter the route where this page should be created (e.g., "/about" or "/contact").
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={pageType} onValueChange={(v) => setPageType(v as "html" | "markdown" | "redirect")} disabled={isCreating}>
                <SelectTrigger id="type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="html">HTML</SelectItem>
                  <SelectItem value="markdown">Markdown</SelectItem>
                  <SelectItem value="redirect">Redirect</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="route">Route</Label>
              <Input
                id="route"
                value={route}
                onChange={(e) => setRoute(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && route.trim() && !isCreating) {
                    handleCreatePage();
                  }
                }}
                autoFocus
                placeholder="/about"
                disabled={isCreating}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreatePage}
              disabled={!route.trim() || isCreating}
            >
              {isCreating ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Route</TableHead>
            <TableHead>Created</TableHead>
            <TableHead>Updated</TableHead>
            <TableHead className="w-[100px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pages && pages.length > 0 ? (
            pages.map((page) => (
              <TableRow 
                key={page._id}
                className="hover:bg-muted/50"
              >
                <TableCell 
                  onClick={() => navigate(`/pages/${page._id}/edit`)}
                  className="cursor-pointer"
                >
                  {page.title}
                </TableCell>
                <TableCell 
                  onClick={() => navigate(`/pages/${page._id}/edit`)}
                  className="font-mono text-sm cursor-pointer"
                >
                  {page.route}
                </TableCell>
                <TableCell 
                  onClick={() => navigate(`/pages/${page._id}/edit`)}
                  className="cursor-pointer"
                >
                  {new Date(page.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell 
                  onClick={() => navigate(`/pages/${page._id}/edit`)}
                  className="cursor-pointer"
                >
                  {new Date(page.updatedAt).toLocaleDateString()}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleClonePage({ pageId: page._id })}
                        disabled={
                          cloningPageId === page._id ||
                          deletingPageId === page._id
                        }
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        {cloningPageId === page._id ? "Cloning..." : "Clone"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        variant="destructive"
                        onClick={() => handleDeletePage({ pageId: page._id })}
                        disabled={deletingPageId === page._id}
                      >
                        {deletingPageId === page._id ? "Deleting..." : "Delete"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="h-24 text-center">
                No pages found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

